import React, { useContext, useEffect, useState } from 'react';
import { deleteField, doc, getDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { AuthContext } from '../context/AuthContext';
import Header from './Header';
import Footer from './Footer';
import ProfileImageUpload from './ProfileImageUpload';
import { isValidIban, normalizeIban } from '../utils/paymentQr';
import { useI18n } from '../i18n/I18nContext';
import { getOnboardingCopy } from '../i18n/onboardingCopy';
import { getLocalizedError } from '../i18n/errorCopy';
import '../style/Marketplace.css';

const CATEGORY_OPTIONS = [
  'Tarot',
  'Astrology',
  'Psychic',
  'Love & relationships',
  'Career & purpose',
  'Dream interpretation',
  'Numerology',
  'Spiritual guidance',
];

const EU_COUNTRIES = [
  'Austria',
  'Belgium',
  'Bulgaria',
  'Croatia',
  'Cyprus',
  'Czechia',
  'Denmark',
  'Estonia',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'Hungary',
  'Ireland',
  'Italy',
  'Latvia',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Netherlands',
  'Poland',
  'Portugal',
  'Romania',
  'Slovakia',
  'Slovenia',
  'Spain',
  'Sweden',
];

const EU_COUNTRY_CODES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
];

const initialForm = {
  name: '',
  headline: '',
  description: '',
  profileImage: '',
  introVideoUrl: '',
  skills: ['Tarot'],
  languages: ['English'],
  experience: '',
  price: '30',
  country: '',
  certifications: '',
  iban: '',
  identityConfirmed: false,
  termsAccepted: false,
};

const splitList = (value) => String(value || '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

const TellerOnboarding = () => {
  const { user, loading: authLoading } = useContext(AuthContext);
  const { language, locale } = useI18n();
  const copy = getOnboardingCopy(language);
  const countryNames = new Intl.DisplayNames([locale], { type: 'region' });
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('draft');
  const [reviewNote, setReviewNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    Promise.all([
      getDoc(doc(db, 'users', user.uid)),
      getDoc(doc(db, 'fortuneTellers', user.uid)),
      getDoc(doc(db, 'tellerApplications', user.uid)),
    ]).then(([userSnap, tellerSnap, applicationSnap]) => {
      const userData = userSnap.exists() ? userSnap.data() : {};
      const tellerData = tellerSnap.exists() ? tellerSnap.data() : {};
      const applicationData = applicationSnap.exists() ? applicationSnap.data() : {};
      const resolvedStatus = tellerData.approvalStatus
        || (tellerData.approved === true ? 'approved' : 'draft');

      setStatus(resolvedStatus);
      setReviewNote(applicationData.reviewNote || '');
      setForm({
        ...initialForm,
        name: tellerData.name || userData.name || user.displayName || '',
        headline: tellerData.headline || '',
        description: tellerData.description || tellerData.bio || '',
        profileImage: tellerData.profileImage || tellerData.photoUrl || userData.photoUrl || '',
        introVideoUrl: tellerData.introVideoUrl || '',
        skills: Array.isArray(tellerData.skills) && tellerData.skills.length
          ? tellerData.skills
          : initialForm.skills,
        languages: Array.isArray(tellerData.languages) && tellerData.languages.length
          ? tellerData.languages
          : initialForm.languages,
        experience: String(tellerData.experience ?? ''),
        price: String(tellerData.price ?? 30),
        country: tellerData.country || '',
        certifications: Array.isArray(tellerData.certifications)
          ? tellerData.certifications.join('\n')
          : (tellerData.certifications || ''),
        iban: tellerData.iban || userData.iban || '',
        identityConfirmed: Boolean(applicationData.identityConfirmed),
        termsAccepted: Boolean(applicationData.termsAccepted),
      });
    }).catch((loadError) => {
      setError(getLocalizedError(loadError, language, copy.loadError));
    }).finally(() => setLoading(false));
  }, [authLoading, copy.loadError, language, navigate, user]);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setMessage('');
    setError('');
  };

  const toggleCategory = (category) => {
    setForm((current) => ({
      ...current,
      skills: current.skills.includes(category)
        ? current.skills.filter((item) => item !== category)
        : [...current.skills, category],
    }));
  };

  const validateForSubmission = () => {
    if (form.name.trim().length < 2) return copy.fullNameError;
    if (form.headline.trim().length < 12) return copy.headlineError;
    if (form.description.trim().length < 120) return copy.introError;
    if (!form.profileImage.trim()) return copy.photoError;
    if (!form.skills.length) return copy.specialtyError;
    if (!form.languages.length) return copy.languageError;
    if (Number(form.experience) < 0 || !Number.isFinite(Number(form.experience))) return copy.experienceError;
    if (Number(form.price) <= 0 || !Number.isFinite(Number(form.price))) return copy.priceError;
    if (!EU_COUNTRIES.includes(form.country)) return copy.countryError;
    if (!isValidIban(form.iban)) return copy.validIban;
    if (!form.identityConfirmed) return copy.identityError;
    if (!form.termsAccepted) return copy.termsError;
    return '';
  };

  const saveApplication = async (submit = false) => {
    if (!user) return;
    const normalizedIban = normalizeIban(form.iban);
    if (normalizedIban && !isValidIban(normalizedIban)) {
      setError(copy.validIban);
      return;
    }
    const validationError = submit ? validateForSubmission() : '';
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(submit ? 'submit' : 'draft');
    setMessage('');
    setError('');
    const nextStatus = submit ? 'under_review' : 'draft';
    const now = new Date().toISOString();
    const tellerRef = doc(db, 'fortuneTellers', user.uid);

    try {
      const existingSnap = await getDoc(tellerRef);
      const payload = {
        userId: user.uid,
        name: form.name.trim(),
        email: user.email || '',
        tutorEmail: user.email || '',
        headline: form.headline.trim(),
        bio: form.description.trim(),
        description: form.description.trim(),
        profileImage: form.profileImage.trim(),
        photoUrl: form.profileImage.trim(),
        introVideoUrl: form.introVideoUrl.trim(),
        skills: form.skills,
        category: form.skills,
        languages: form.languages,
        experience: Number(form.experience || 0),
        price: Number(form.price),
        country: form.country.trim(),
        certifications: form.certifications.trim(),
        role: 'teller',
        type: 'fortuneTeller',
        approvalStatus: nextStatus,
        profileComplete: submit,
        updatedAt: now,
        ...(submit ? { submittedAt: serverTimestamp() } : {}),
      };

      if (!existingSnap.exists()) {
        Object.assign(payload, {
          approved: false,
          banned: false,
          rating: 0,
          reviewsCount: 0,
          applicationCreatedAt: now,
          createdAt: now,
        });
      } else {
        // Payment details belong only in the private /users record.
        Object.assign(payload, {
          iban: deleteField(),
          bankAccount: deleteField(),
        });
      }

      const applicationBatch = writeBatch(db);
      applicationBatch.set(tellerRef, payload, { merge: true });
      applicationBatch.set(doc(db, 'tellerApplications', user.uid), {
        userId: user.uid,
        email: user.email || '',
        identityConfirmed: form.identityConfirmed,
        termsAccepted: form.termsAccepted,
        approvalStatus: nextStatus,
        updatedAt: now,
        ...(submit ? { submittedAt: serverTimestamp() } : {}),
      }, { merge: true });
      applicationBatch.set(doc(db, 'users', user.uid), {
        name: form.name.trim(),
        bio: form.description.trim(),
        description: form.description.trim(),
        photoUrl: form.profileImage.trim(),
        profileImage: form.profileImage.trim(),
        experience: Number(form.experience || 0),
        languages: form.languages,
        specializations: form.skills,
        iban: normalizedIban,
        updatedAt: now,
      }, { merge: true });
      await applicationBatch.commit();

      setStatus(nextStatus);
      setMessage(submit
        ? copy.submitted
        : copy.draftSaved);
    } catch (saveError) {
      setError(getLocalizedError(saveError, language, copy.saveError));
    } finally {
      setSaving('');
    }
  };

  if (authLoading || loading) {
    return <div className="market-page-state">{copy.loading}</div>;
  }

  if (status === 'under_review' || status === 'approved') {
    const statusText = status === 'approved'
      ? { eyebrow: copy.approvedEyebrow, title: copy.approvedTitle, body: copy.approvedBody }
      : { eyebrow: copy.submittedEyebrow, title: copy.submittedTitle, body: copy.submittedBody };
    return (
      <div>
        <Header />
        <main className="teller-onboarding-shell">
          <section className={`application-status-card ${status}`}>
            <span className="eyebrow">{statusText.eyebrow}</span>
            <h1>{statusText.title}</h1>
            <p>{statusText.body}</p>
            <div className="application-status-steps" aria-label={copy.progress}>
              <span className="complete">{copy.complete}</span>
              <span className={status === 'approved' ? 'complete' : 'active'}>{copy.review}</span>
              <span className={status === 'approved' ? 'complete' : ''}>{copy.live}</span>
            </div>
            {status === 'approved' && (
              <Link className="market-primary-button" to="/teller-dashboard">{copy.dashboard}</Link>
            )}
            {message && <div className="market-inline-message success">{message}</div>}
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Header />
      <main className="teller-onboarding-shell">
        <header className="teller-onboarding-intro">
          <div>
            <span className="eyebrow">{copy.application}</span>
            <h1>{copy.title}</h1>
            <p>{copy.intro}</p>
          </div>
          <aside>
            <strong>{copy.reviewHeading}</strong>
            {copy.reviewItems.map((item) => <span key={item}>{item}</span>)}
          </aside>
        </header>

        {(status === 'changes_requested' || status === 'rejected') && (
          <section className={`application-review-note ${status}`}>
            <strong>{status === 'changes_requested' ? copy.changes : copy.rejected}</strong>
            <p>{reviewNote || copy.reviewDefault}</p>
          </section>
        )}

        {error && <div className="market-inline-message error" role="alert">{error}</div>}
        {message && <div className="market-inline-message success" role="status">{message}</div>}

        {status !== 'approved' && (
          <section className="application-approval-banner" aria-label={copy.approvalLabel}>
            <span>{copy.approvalLabel}</span>
            <h2>{copy.approvalTitle}</h2>
            <p>{copy.approvalBody}</p>
          </section>
        )}

        <form className="teller-application-form" onSubmit={(event) => {
          event.preventDefault();
          saveApplication(true);
        }}>
          <section className="application-form-section">
            <div className="application-section-number">01</div>
            <div className="application-section-content">
              <h2>{copy.publicTitle}</h2>
              <p>{copy.publicBody}</p>
              <div className="application-grid two">
                <label>
                  {copy.name}
                  <input value={form.name} onChange={(event) => setField('name', event.target.value)} maxLength="80" required />
                </label>
                <label>
                  {copy.headline}
                  <input value={form.headline} onChange={(event) => setField('headline', event.target.value)} maxLength="100" placeholder={copy.headlinePlaceholder} required />
                </label>
              </div>
              <ProfileImageUpload
                userId={user.uid}
                value={form.profileImage}
                onChange={(downloadUrl) => setField('profileImage', downloadUrl)}
                required
              />
              <label>
                {copy.about}
                <textarea className="application-practice-field" value={form.description} onChange={(event) => setField('description', event.target.value)} rows="4" maxLength="1400" placeholder={copy.aboutPlaceholder} required />
                <small>{form.description.trim().length}/120 {copy.minimum}</small>
              </label>
            </div>
          </section>

          <section className="application-form-section">
            <div className="application-section-number">02</div>
            <div className="application-section-content">
              <h2>{copy.expertise}</h2>
              <p>{copy.expertiseBody}</p>
              <fieldset className="application-category-picker">
                <legend>{copy.specialties}</legend>
                {CATEGORY_OPTIONS.map((category, index) => (
                  <label key={category} className={form.skills.includes(category) ? 'selected' : ''}>
                    <input type="checkbox" checked={form.skills.includes(category)} onChange={() => toggleCategory(category)} />
                    <span>{copy.categories[index]}</span>
                  </label>
                ))}
              </fieldset>
              <div className="application-grid application-details-grid">
                <label>
                  {copy.languages}
                  <input value={form.languages.join(', ')} onChange={(event) => setField('languages', splitList(event.target.value))} placeholder={copy.languagePlaceholder} />
                </label>
                <label>
                  {copy.years}
                  <input type="number" min="0" max="80" value={form.experience} onChange={(event) => setField('experience', event.target.value)} />
                </label>
                <label>
                  {copy.price}
                  <input type="number" min="1" max="1000" step="1" value={form.price} onChange={(event) => setField('price', event.target.value)} />
                </label>
                <label className="application-country-field">
                  {copy.country}
                  <select value={form.country} onChange={(event) => setField('country', event.target.value)} required>
                    <option value="">{copy.chooseCountry}</option>
                    {EU_COUNTRIES.map((country, index) => (
                      <option key={country} value={country}>{countryNames.of(EU_COUNTRY_CODES[index])}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="application-certifications-field">
                {copy.certifications}
                <textarea
                  value={form.certifications}
                  onChange={(event) => setField('certifications', event.target.value)}
                  rows="8"
                  maxLength="1200"
                  placeholder={copy.certificationsPlaceholder}
                />
                <small>{form.certifications.length}/1200 {copy.characters}</small>
              </label>
              <div className="application-availability-guidance">
                <strong>{copy.availabilityTitle}</strong>
                <span>{copy.availabilityBody}</span>
              </div>
            </div>
          </section>

          <section className="application-form-section">
            <div className="application-section-number">03</div>
            <div className="application-section-content">
              <h2>{copy.verification}</h2>
              <p>{copy.verificationBody}</p>
              <label>
                <span className="application-label-row">
                  <span>{copy.video}</span>
                  <span className="application-optional-label">{copy.optional}</span>
                </span>
                <input type="url" value={form.introVideoUrl} onChange={(event) => setField('introVideoUrl', event.target.value)} placeholder={copy.videoPlaceholder} />
                <small>{copy.videoHelp}</small>
              </label>
              <label>
                {copy.payout}
                <input value={form.iban} onChange={(event) => setField('iban', event.target.value)} placeholder="SK00 0000 0000 0000 0000 0000" />
              </label>
              <label className="application-consent">
                <input type="checkbox" checked={form.identityConfirmed} onChange={(event) => setField('identityConfirmed', event.target.checked)} />
                <span>{copy.identityConsent}</span>
              </label>
              <label className="application-consent">
                <input type="checkbox" checked={form.termsAccepted} onChange={(event) => setField('termsAccepted', event.target.checked)} />
                <span>{copy.termsConsent}</span>
              </label>
            </div>
          </section>

          <div className="application-form-actions">
            <button type="button" className="market-secondary-button" onClick={() => saveApplication(false)} disabled={Boolean(saving)}>
              {saving === 'draft' ? copy.saving : copy.saveDraft}
            </button>
            <button type="submit" className="market-primary-button" disabled={Boolean(saving)}>
              {saving === 'submit' ? copy.submitting : copy.submit}
            </button>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
};

export default TellerOnboarding;
